import { useMemo } from 'react';
import { CustomFieldDefinition } from '../../../types';
import {
  createCustomFieldColumns,
  customFieldCellEditableByType
} from '../../Compounds/UpstackDataGrid/customFieldColumns';
import { foreignRowAccountIds } from '../../Compounds/UpstackDataGrid/foreignRowAccountIds';
import { useCustomFieldAcceptance } from '../../../Api/Admin/CustomFields/useCustomFieldAcceptance';
import { fetchCustomFieldDefinitionsApi } from '../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { fetchCustomFieldSettingApi } from '../../../Api/Admin/CustomFields/customFieldSettingsApi';

export const PROJECT_DETAIL = 'ProjectDetail';
const NO_ACCOUNTS: string[] = [];

const isRelatedInventory = (def: Pick<CustomFieldDefinition, 'entity_type'>) => def.entity_type === PROJECT_DETAIL;

// Related Inventory fields an admin has opted onto this grid sort among themselves, after every Inventory field.
export const inventoryCustomFieldColumns = (defs: CustomFieldDefinition[]) => [
  ...createCustomFieldColumns(defs.filter((def) => !isRelatedInventory(def))),
  ...createCustomFieldColumns(defs.filter(isRelatedInventory))
];

export interface InventoryCustomFieldDefs {
  definitions: CustomFieldDefinition[];
  // The account opted in but its Related Inventory definitions did not load, so only the Inventory ones are here.
  relatedInventoryFailed: boolean;
}

// Null when the Inventory definitions themselves did not load, which leaves the grid's current set in place. A setting
// that cannot be read counts as off: every reader of this page asks for it, so an outage must not fail them all.
export const fetchInventoryCustomFieldDefs = async (accountId: string): Promise<InventoryCustomFieldDefs | null> => {
  const [inventoryRes, settingRes] = await Promise.all([
    fetchCustomFieldDefinitionsApi(accountId),
    fetchCustomFieldSettingApi(accountId, PROJECT_DETAIL)
  ]);
  if (!inventoryRes.data) return null;
  if (!settingRes.data?.display_on_inventory) return { definitions: inventoryRes.data, relatedInventoryFailed: false };

  const detailRes = await fetchCustomFieldDefinitionsApi(accountId, undefined, PROJECT_DETAIL);
  if (!detailRes.data) return { definitions: inventoryRes.data, relatedInventoryFailed: true };
  return { definitions: [...inventoryRes.data, ...detailRes.data], relatedInventoryFailed: false };
};

interface InventoryCellEditableOptions<T> {
  definitions: CustomFieldDefinition[];
  rows: T[];
  rowAccountId: (row: T) => string | undefined;
  definitionsAccountId: string | null;
  unrestricted: boolean;
  denied: boolean;
}

// Rebuilt each render rather than memoized so an account switch can never leave the gate on the previous account's set.
export const useInventoryCustomFieldCellEditable = <T>({
  definitions,
  rows,
  rowAccountId,
  definitionsAccountId,
  unrestricted,
  denied
}: InventoryCellEditableOptions<T>) => {
  const otherRowAccountIds = useMemo(
    () => foreignRowAccountIds(rows, rowAccountId, definitionsAccountId),
    [rows, definitionsAccountId]
  );
  const inventoryDefs = definitions.filter((def) => !isRelatedInventory(def));
  const detailDefs = definitions.filter(isRelatedInventory);
  const inventoryAcceptance = useCustomFieldAcceptance(otherRowAccountIds, 'Inventory');
  const detailAcceptance = useCustomFieldAcceptance(
    detailDefs.length ? otherRowAccountIds : NO_ACCOUNTS,
    PROJECT_DETAIL
  );

  const binding = { definitionsAccountId, rowAccountId, unrestricted, denied };
  return customFieldCellEditableByType<T>([
    { ...binding, acceptance: inventoryAcceptance, definitions: inventoryDefs },
    { ...binding, acceptance: detailAcceptance, definitions: detailDefs }
  ]);
};
